import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Alert, CircularProgress } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createPriceList, updatePriceList, type PriceListItem } from '../../api/preturi'
import { errorMessage } from '../../api/client'

const schema = z.object({
  code: z.string().min(1, 'Câmp obligatoriu').max(32),
  name: z.string().min(1, 'Câmp obligatoriu').max(128),
  marketSegment: z.union([z.enum(['Lapte', 'Delicatese', 'Blistere', 'Altele']), z.literal('')]).optional(),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = { code: '', name: '', marketSegment: '' }

export default function PriceListFormDialog({ list, onClose }: { list: PriceListItem | null | 'new'; onClose: () => void }) {
  const open = list !== null
  const editing = list !== null && list !== 'new'
  const qc = useQueryClient()

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })

  useEffect(() => {
    if (editing) {
      const l = list as PriceListItem
      reset({ code: l.code, name: l.name, marketSegment: l.marketSegment ?? '' })
    } else if (list === 'new') {
      reset(EMPTY)
    }
  }, [list, editing, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload = { code: values.code, name: values.name, marketSegment: values.marketSegment || null }
      if (editing) return updatePriceList((list as PriceListItem).id, payload)
      return createPriceList(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['comercial', 'preturi', 'liste'] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare listă de preț' : 'Listă de preț nouă'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Controller name="code" control={control} render={({ field }) => (
              <TextField {...field} label="Cod" fullWidth error={!!errors.code} helperText={errors.code?.message} />
            )} />
            <Controller name="name" control={control} render={({ field }) => (
              <TextField {...field} label="Denumire" fullWidth error={!!errors.name} helperText={errors.name?.message} />
            )} />
            <Controller name="marketSegment" control={control} render={({ field }) => (
              <TextField {...field} select label="Segment de piață" fullWidth helperText="Fără segment = se aplică tuturor clienților fără listă proprie">
                <MenuItem value="">— (generală)</MenuItem>
                <MenuItem value="Lapte">Lapte</MenuItem>
                <MenuItem value="Delicatese">Delicatese</MenuItem>
                <MenuItem value="Blistere">Blistere</MenuItem>
                <MenuItem value="Altele">Altele</MenuItem>
              </TextField>
            )} />
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
